import React, { useState, useEffect, useMemo } from 'react'
import PurchaseModal from './PurchaseModal'
import { getQrCodeKitShopItems } from './qrCodeKitShopCatalog'
import { baseUrl } from '../../../Environments/environment'
import { apiClient } from '../../../api'
import { useProperty } from '../../../Contexts/PropertyContext/PropertyContext'
import {
  formatShopAmount,
  getPropertyCountry,
} from '../../CreditSetup/paymentCurrency.util'

const QRCodeKitSkeleton = () => {
  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto">
        {/* Header Skeleton */}
        <div className="sm:flex sm:items-center justify-between mt-6">
          <div className="sm:flex-auto">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2"></div>
            <div className="h-4 w-64 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>

        {/* Items Grid Skeleton */}
        <div className="mt-8 space-y-6">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image Section Skeleton */}
                <div className="w-full sm:w-64 h-64 sm:h-auto flex-shrink-0 bg-gray-200"></div>

                {/* Content Section Skeleton */}
                <div className="flex-1 p-6 flex flex-col">
                  <div className="flex justify-between items-start mb-4 gap-4">
                    {/* Heading Skeleton */}
                    <div className="h-6 w-32 sm:w-40 bg-gray-200 rounded"></div>

                    {/* Checkbox and Price Skeleton */}
                    <div className="flex flex-col items-end gap-3 flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="text-right space-y-1">
                          <div className="h-6 w-16 bg-gray-200 rounded"></div>
                          <div className="h-3 w-20 bg-gray-200 rounded hidden sm:block"></div>
                        </div>
                        <div className="h-5 w-5 bg-gray-200 rounded"></div>
                      </div>
                    </div>
                  </div>

                  {/* Detail Skeleton */}
                  <div>
                    <div className="h-4 w-32 bg-gray-200 rounded mb-2"></div>
                    <div className="space-y-2">
                      <div className="h-3 w-full bg-gray-200 rounded"></div>
                      <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
                      <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const QRCodeKit = ({ propertyId, isStepContent = false, onComplete, onPrevious }) => {
  let { selectedPropertyId } = useProperty();
  selectedPropertyId = propertyId||selectedPropertyId;
  const propertyCountry = getPropertyCountry(selectedPropertyId)
  const items = useMemo(
    () => getQrCodeKitShopItems(propertyCountry),
    [propertyCountry],
  )
  const currency = items[0]?.currency ?? "usd"

  const [selectedItems, setSelectedItems] = useState(new Set())
  const [quantities, setQuantities] = useState({})
  const [quantityInputs, setQuantityInputs] = useState({})
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false)
  const [orderDetails, setOrderDetails] = useState(null)
  const [orderedItemIds, setOrderedItemIds] = useState(new Set())
  const [loading, setLoading] = useState(true)

  const handleCheckboxChange = (itemId) => {
    // Prevent selection if item is already purchased
    if (orderedItemIds.has(itemId)) {
      return
    }
    
    setSelectedItems((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(itemId)) {
        newSet.delete(itemId)
      } else {
        newSet.add(itemId)
        // Initialize quantity to 1 if not set
        if (!quantities[itemId]) {
          setQuantities((prevQty) => ({ ...prevQty, [itemId]: 1 }))
          setQuantityInputs((prev) => ({ ...prev, [itemId]: '1' }))
        }
      }
      return newSet
    })
  }

  const isItemPurchased = (itemId) => {
    return orderedItemIds.has(itemId)
  }

  const handleQuantityChange = (itemId, value) => {
    // Allow empty string for clearing
    if (value === '') {
      setQuantityInputs((prev) => ({ ...prev, [itemId]: '' }))
      setQuantities((prev) => ({ ...prev, [itemId]: 1 })) // Keep calculation at 1
      return
    }

    const numValue = parseInt(value)
    if (isNaN(numValue) || numValue < 1) {
      return
    }

    setQuantityInputs((prev) => ({ ...prev, [itemId]: value }))
    setQuantities((prev) => ({ ...prev, [itemId]: numValue }))
  }

  const fetchOrderDetails = async () => {
    setLoading(true)
    try {
      const pid = selectedPropertyId || localStorage.getItem("selectedPropertyId")
      const token = localStorage.getItem("accessToken")
      const response = await apiClient.get(
        `${baseUrl}/qrKitOrder/${pid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      )
      if (response.status === 200) {
        const orders = response.data.result?.qrKitOrders || []
        setOrderDetails(orders)
        
        // Extract itemIds only from orders with status "completed"
        const orderedIds = new Set()
        orders.forEach((order) => {
          // Only include items from completed orders
          if ((order.orderStatus === 'placed' || order.orderStatus === 'dispatched' || order.orderStatus === 'processing') && order.orderItems && Array.isArray(order.orderItems)) {
            order.orderItems.forEach((orderItem) => {
              if (orderItem.itemId) {
                orderedIds.add(orderItem.itemId)
              }
            })
          }
        })
        
        setOrderedItemIds(orderedIds)
        console.log("Order details fetched successfully", orders)
        console.log("Ordered item IDs (completed orders only):", Array.from(orderedIds))
      }
    } catch (error) {
      console.error("Error fetching order details", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrderDetails()
  }, [selectedPropertyId])

  const handleQuantityBlur = (itemId) => {
    const inputValue = quantityInputs[itemId]
    if (inputValue === '' || inputValue === undefined || parseInt(inputValue) < 1) {
      setQuantityInputs((prev) => ({ ...prev, [itemId]: '1' }))
      setQuantities((prev) => ({ ...prev, [itemId]: 1 }))
    }
  }

  const getQuantity = (itemId) => {
    return quantities[itemId] || 1
  }

  const getQuantityInputValue = (itemId) => {
    return quantityInputs[itemId] !== undefined ? quantityInputs[itemId] : (quantities[itemId] || '1')
  }

  const calculateTotal = () => {
    return Array.from(selectedItems).reduce((total, itemId) => {
      const item = items.find((i) => i.id === itemId)
      const quantity = getQuantity(itemId)
      return total + (item ? item.price * quantity : 0)
    }, 0)
  }

  const totalPrice = calculateTotal()

  const validOrders = orderDetails 
    ? orderDetails.filter(order => ['placed', 'dispatched', 'processing', 'completed'].includes(order.orderStatus))
        .sort((a, b) => new Date(b.createdAt || b.orderDate) - new Date(a.createdAt || a.orderDate))
    : [];

  if (loading) {
    return <QRCodeKitSkeleton />
  }

  return (
    <div className={isStepContent ? "mx-auto bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl mb-12" : "px-4 sm:px-6 lg:px-8 min-h-screen bg-gradient-to-b from-gray-50 to-white"}>
      <div className={isStepContent ? "" : "max-w-7xl mx-auto"}>
        {isStepContent ? (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between border-b border-gray-100 px-4 py-4 sm:px-8 sm:py-6 gap-4 sm:gap-0">
            <div className="mt-2 sm:mt-0">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">Get Your QR Stand</h2>
                <p className="mt-2 text-sm md:text-base text-gray-600 max-w-2xl">
                    You can order one of our elegant QR kits.
                </p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto justify-end">
                <a
                    href="http://review.onelyk.com/customer/new"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 px-4 py-2 w-full sm:w-auto text-center"
                >
                    Help
                </a>
                {onPrevious && (
                    <button
                        type="button"
                        onClick={onPrevious}
                        className="w-full sm:w-auto rounded-md bg-gray-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    >
                        Previous
                    </button>
                )}
                {onComplete && (
                    <button
                        type="button"
                        onClick={onComplete}
                        className="w-full sm:w-auto rounded-md bg-gray-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
                    >
                        Skip and Finish Setup
                    </button>
                )}
                {selectedItems.size > 0 && (
                    <button
                        onClick={() => setIsPurchaseModalOpen(true)}
                        className="w-full sm:w-auto rounded-md bg-indigo-600 px-6 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    >
                        Proceed (     {formatShopAmount(totalPrice, currency)})
                    </button>
                )}
            </div>
          </div>
        ) : (
          <div className="sm:flex sm:items-center justify-between mt-6 animate-fadeIn">
            <div className="sm:flex-auto">
              <h1 className="text-2xl text-gray-800 tracking-tight">QR Code Kit</h1>
              <p className="mt-2 text-sm text-gray-600 leading-6">
                Purchase items for your property's reviews.
              </p>
            </div>
            {selectedItems.size > 0 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-6 sm:mt-0 w-full sm:w-auto">
                <div className="text-center sm:text-right w-full sm:w-auto">
                  <p className="text-sm text-gray-600">Total Amount</p>
                  <p className="text-2xl font-semibold text-blue-600">
                  {formatShopAmount(totalPrice, currency)}
                  </p>
                </div>
                <button
                  onClick={() => setIsPurchaseModalOpen(true)}
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
                >
                  Proceed
                </button>
              </div>
            )}
          </div>
        )}

        {/* Items Grid */}
        <div className={isStepContent ? "px-4 py-4 sm:py-6 sm:p-8 space-y-6" : "mt-8 space-y-6"}>
          {items.map((item) => {
            const isPurchased = isItemPurchased(item.id)
            return (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col sm:flex-row">
                {/* Image Section - Left */}
                <div className="w-full sm:w-64 h-64 sm:h-auto flex-shrink-0">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content Section - Right */}
                <div className="flex-1 p-6 flex flex-col">
                  {/* Top Header: Title and Purchase Controls */}
                  <div className="flex justify-between items-start mb-4 gap-4">
                    {/* 1. Heading (Name of the item) */}
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-xl font-medium text-gray-900">
                          {item.name}
                        </h2>
                        {isPurchased && (
                          <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700 font-medium whitespace-nowrap">
                            Purchased
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Checkbox, Price, and Quantity */}
                    <div className="flex flex-col items-end gap-3 flex-shrink-0">
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <span className="text-lg font-medium text-blue-600 block">
                          {formatShopAmount(item.price, item.currency)}
                          </span>
                          {selectedItems.has(item.id) && (
                            <span className="text-xs text-gray-500">
                            × {getQuantity(item.id)} = {formatShopAmount(item.price * getQuantity(item.id), item.currency)}
                            </span>
                          )}
                        </div>
                        <input
                          type="checkbox"
                          checked={selectedItems.has(item.id)}
                          onChange={() => handleCheckboxChange(item.id)}
                          disabled={isPurchased}
                          className={`w-5 h-5 text-blue-600 border-2 rounded focus:ring-blue-500 bg-white ${
                            isPurchased
                              ? 'border-gray-300 cursor-not-allowed opacity-50'
                              : 'border-blue-400 cursor-pointer checked:bg-blue-600 checked:border-blue-600'
                          }`}
                        />
                      </div>
                      {/* Quantity input below checkbox */}
                      {selectedItems.has(item.id) && (
                        <div className="flex items-center gap-2">
                          <label className="text-sm text-gray-600">Qty:</label>
                          <input
                            type="number"
                            min="1"
                            value={getQuantityInputValue(item.id)}
                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                            onBlur={() => handleQuantityBlur(item.id)}
                            className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 2. Detail of the item */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">
                      {item.detail.heading}
                    </h3>
                    <ul className="list-disc list-inside space-y-1">
                      {item.detail.points.map((point, index) => (
                        <li key={index} className="text-sm text-gray-600">
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            )
          })}
        </div>

        {/* Bottom Proceed Button */}
        {selectedItems.size > 0 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-4 mt-8">
            <div className="text-center sm:text-right w-full sm:w-auto">
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-semibold text-blue-600">
                {formatShopAmount(totalPrice, currency)}
              </p>
            </div>
            <button
              onClick={() => setIsPurchaseModalOpen(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Proceed
            </button>
          </div>
        )}

        {/* Order History Section */}
        {validOrders.length > 0 && (
          <div className="mt-12 mb-8">
            <h2 className="text-xl font-medium text-gray-900 mb-6">Previous Purchases</h2>
            <div className="space-y-4">
              {validOrders.map((order, idx) => (
                <div key={order._id || order.id || idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                    <div>
                      <p className="text-sm text-gray-500">
                        Order Date: <span className="font-medium text-gray-900">{new Date(order.createdAt || order.orderDate).toLocaleDateString()}</span>
                      </p>
                      <p className="text-sm text-gray-500">
                        Status: <span className={`font-medium capitalize ${order.orderStatus === 'completed' || order.orderStatus === 'dispatched' ? 'text-green-600' : 'text-blue-600'}`}>{order.orderStatus}</span>
                      </p>
                    </div>
                    <div className="mt-2 sm:mt-0 text-left sm:text-right">
                      <p className="text-sm text-gray-500 mb-1">Total Amount</p>
                      <p className="text-lg font-medium text-blue-600">{formatShopAmount(order.orderAmount, order.currency)}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-gray-100 pt-4">
                    <h3 className="text-sm font-medium text-gray-900 mb-3">Items Purchased</h3>
                    <ul className="space-y-2">
                      {order.orderItems && order.orderItems.map((item, itemIdx) => (
                        <li key={itemIdx} className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">
                            <span className="font-medium text-gray-900">{item.itemQuantity}x</span> {item.itemName}
                          </span>
                          <span className="font-medium text-gray-900">{formatShopAmount(item.itemTotal||(item.itemQuantity * item.itemPrice) ||0, order.currency)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Purchase Modal */}
        <PurchaseModal
          propertyId={selectedPropertyId}
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          selectedItems={selectedItems}
          quantities={quantities}
          items={items}
          totalPrice={totalPrice}
          onOrderSuccess={async () => {
            // Clear selected items and quantities after successful order
            setSelectedItems(new Set())
            setQuantities({})
            setQuantityInputs({})
            // Wait a bit for the database to update, then refresh order details
            setTimeout(() => {
              fetchOrderDetails()
            }, 2000)
          }}
        />

      </div>
    </div>
  )
}

export default QRCodeKit
